'use client';

import { useEffect } from 'react';

/**
 * Browser-side Sentry initialization.
 *
 * Mounted near the root layout so it runs once per session. Captures:
 * - Uncaught JS errors (window.onerror)
 * - Unhandled promise rejections
 * - React render errors (via Sentry's React integration when available)
 *
 * NEXT_PUBLIC_SENTRY_DSN is the same DSN as server-side SENTRY_DSN — Sentry
 * routes events by source (server vs. browser) using the SDK identifier.
 */
export function SentryClientInit() {
  useEffect(() => {
    const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
    if (!dsn) return;

    // Lazy import so the SDK only loads when the DSN is configured.
    void import('@sentry/browser').then((Sentry) => {
      Sentry.init({
        dsn,
        environment: process.env.NODE_ENV ?? 'production',
        // Capture all errors. Performance tracing disabled for now — turn on
        // tracesSampleRate: 0.1 if/when we add @sentry/nextjs or want spans.
        tracesSampleRate: 0,
        // Filter noise: extension errors, ResizeObserver loop messages, etc.
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          // Network errors — usually user lost connection, not a real bug
          'NetworkError',
          'Failed to fetch',
        ],
      });
    });
  }, []);

  return null;
}
